// ─── Chin Bot Workspace Persistence ──────────────────────────
// Saves the ENTIRE workspace state to Supabase IMMEDIATELY on
// every change. No localStorage. No debounce for critical data.
//
// On login/mount, restores the full workspace so the user can
// continue exactly where they left off — across any device.
//
// Each Spotware account has its own independent workspace stored
// under a composite key: {strategyId}__account_{accountId}.
//
// Workspace includes:
//   - selectedListId (which symbol list is active)
//   - statusMap (per-symbol status: one of the 6 Chin mẫu hình patterns or null)
//   - playMap (per-symbol on/off toggle)
//   - symbolConfigs (per-symbol conditionPrice + maxOrders)

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { buildSessionKey } from '@/lib/session-key';
import { getDeviceWriterId } from '@/lib/device-id';

// ─── Types ──────────────────────────────────────────────────

export type StatusLevel =
  | 'null'
  | 'box_vs_candle'
  | 'tma50_candle'
  | 'ma_perfect'
  | 'ma_small'
  | 'ma_large'
  | 'candle_trend';

/** Valid status keys — used to normalize legacy `level1`/`level2` values back to `null` on load. */
const VALID_STATUS_LEVELS = new Set<string>([
  'null',
  'box_vs_candle',
  'tma50_candle',
  'ma_perfect',
  'ma_small',
  'ma_large',
  'candle_trend',
]);

export interface SymbolPlayConfig {
  conditionPrice: string;
  maxOrders: string;
  direction: 'buy' | 'sell' | null;
  orderDirection: 'buy' | 'sell' | null;
  orderVolume: string;
  /** Box threshold (pips). Optional — default 10 when empty. */
  boxX?: string;
  /** Box Telegram alert enabled for this symbol. Optional — default true. */
  boxAlertOn?: boolean;
  /** Free-text user note for this symbol. Optional. */
  note?: string;
}

export interface ChinWorkspaceData {
  selectedListId: string;
  selectedAccountId: string;
  statusMap: Record<number, StatusLevel>;
  playMap: Record<number, boolean>;
  symbolConfigs: Record<number, SymbolPlayConfig>;
  // Telegram alert toggles (engine keeps running; only mute Telegram messages)
  tma50AlertOn?: boolean;
  convergenceAlertOn?: boolean;
  boxVsTma50AlertOn?: boolean;
  // Risk Control Rule 4 (After Hours daily shutdown) enabled flag
  rule4Enabled?: boolean;
}

// Sync status surfaced to the UI so the toggle buttons can show a
// small cloud-save indicator (syncing → saved/error → idle).
export type SaveStatus = 'idle' | 'syncing' | 'saved' | 'error';

// ─── Keepalive auth cache ──────────────────────────────────────
// Caches the current user id + access token so the unload "keepalive"
// flush can write directly to Supabase REST (via fetch keepalive:true)
// WITHOUT an async getUser()/getSession() round-trip. During beforeunload/
// pagehide the browser will not let an awaited Promise resolve, but it
// DOES guarantee that a `keepalive` fetch completes even after the page
// is destroyed — this is how the toggle survives "off + close instantly".
interface CachedAuth {
  userId: string;
  accessToken: string;
}
let authCache: CachedAuth | null = null;

// ─── Per-device writer id ──────────────────────────────────────
// (moved to src/lib/device-id.ts — imported above as getDeviceWriterId)

// ─── Hook ───────────────────────────────────────────────────

export function useChinWorkspacePersistence(
  strategyId: string,
  accountId: string,
  onExternalChange?: (data: ChinWorkspaceData) => void,
) {
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the latest external-change callback in a ref so the realtime
  // subscription below never holds a stale closure.
  const onExternalChangeRef = useRef(onExternalChange);
  onExternalChangeRef.current = onExternalChange;

  // Cache the latest Telegram alert toggle values so ANY save (even from
  // handlers that don't pass them) persists the correct state.
  const alertTogglesRef = useRef<{ tma50AlertOn: boolean; convergenceAlertOn: boolean; boxVsTma50AlertOn: boolean }>({
    tma50AlertOn: true,
    convergenceAlertOn: true,
    boxVsTma50AlertOn: true,
  });

  // Cache the latest Rule 4 enabled flag so ANY save (even from handlers that
  // don't pass it) persists the correct value.
  const rule4EnabledRef = useRef<boolean>(true);

  // Cache the LATEST full workspace payload (with resolved toggles) so the
  // keepalive flush on unload can rebuild the complete session_data without
  // needing React state — which may already be stale/cleared during unload.
  const latestDataRef = useRef<ChinWorkspaceData | null>(null);

  // Cloud save status for the UI sync indicator.
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const statusResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markSaved = useCallback(() => {
    setSaveStatus('saved');
    if (statusResetTimerRef.current) clearTimeout(statusResetTimerRef.current);
    statusResetTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
  }, []);

  const markError = useCallback(() => {
    setSaveStatus('error');
    if (statusResetTimerRef.current) clearTimeout(statusResetTimerRef.current);
    statusResetTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2500);
  }, []);

  // Composite key: strategy + account
  const sessionKey = accountId ? buildSessionKey(strategyId, accountId) : strategyId;

  // ─── Save workspace to Supabase ──────────────────────────

  const saveWorkspace = useCallback(async (data: ChinWorkspaceData) => {
    // ── Update cached toggle values SYNCHRONOUSLY (before any await) ──
    // CRITICAL: these must be updated BEFORE `await supabase.auth.getUser()`.
    // Many save paths (engine events, beforeunload, risk control, status/play
    // changes) do NOT pass the toggle values and rely on this ref. If the ref
    // is updated only after an await, a concurrent save can read the STALE
    // value (e.g. `true`) and overwrite a freshly-toggled OFF back to ON.
    if (data.tma50AlertOn !== undefined) alertTogglesRef.current.tma50AlertOn = data.tma50AlertOn;
    if (data.convergenceAlertOn !== undefined) alertTogglesRef.current.convergenceAlertOn = data.convergenceAlertOn;
    if (data.boxVsTma50AlertOn !== undefined) alertTogglesRef.current.boxVsTma50AlertOn = data.boxVsTma50AlertOn;
    if (data.rule4Enabled !== undefined) rule4EnabledRef.current = data.rule4Enabled;

    // Cache latest full payload (with resolved toggles) for keepalive flush on unload.
    latestDataRef.current = {
      selectedListId: data.selectedListId,
      selectedAccountId: data.selectedAccountId,
      statusMap: data.statusMap,
      playMap: data.playMap,
      symbolConfigs: data.symbolConfigs,
      tma50AlertOn: alertTogglesRef.current.tma50AlertOn,
      convergenceAlertOn: alertTogglesRef.current.convergenceAlertOn,
      boxVsTma50AlertOn: alertTogglesRef.current.boxVsTma50AlertOn,
      rule4Enabled: rule4EnabledRef.current,
    };

    setSaveStatus('syncing');

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const user = sessionData.session?.user;
      if (!user || !sessionData.session) {
        setSaveStatus('idle');
        return;
      }

      // Cache auth for the synchronous keepalive flush (no await needed on unload).
      authCache = {
        userId: user.id,
        accessToken: sessionData.session.access_token,
      };

      await supabase
        .from('bot_session_state')
        .upsert({
          user_id: user.id,
          strategy_id: sessionKey,
          session_data: {
            type: 'chin',
            writerId: getDeviceWriterId(),
            selectedAccountId: data.selectedAccountId,
            wasRunning: true,
            lastActive: Date.now(),
            rule4Enabled: rule4EnabledRef.current,
            alertToggles: {
              tma50AlertOn: alertTogglesRef.current.tma50AlertOn,
              convergenceAlertOn: alertTogglesRef.current.convergenceAlertOn,
              boxVsTma50AlertOn: alertTogglesRef.current.boxVsTma50AlertOn,
            },
            workspace: {
              selectedListId: data.selectedListId,
              statusMap: data.statusMap,
              playMap: data.playMap,
              symbolConfigs: data.symbolConfigs,
            },
          } as unknown as Record<string, unknown>,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,strategy_id' });
      markSaved();
    } catch (err) {
      console.warn('[ChinWorkspace] Failed to save to Supabase:', err);
      markError();
    }
  }, [sessionKey]);

  // ─── Debounced save for rapid input changes ──────────────

  const scheduleSave = useCallback((data: ChinWorkspaceData) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveWorkspace(data);
    }, 300);
  }, [saveWorkspace]);

  // ─── Immediate save for toggle/select changes ────────────

  const saveImmediate = useCallback((data: ChinWorkspaceData) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    saveWorkspace(data);
  }, [saveWorkspace]);

  // ─── Load workspace from Supabase ────────────────────────

  const loadWorkspace = useCallback(async (): Promise<ChinWorkspaceData | null> => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const user = sessionData.session?.user;
      if (!user || !sessionData.session) return null;

      // Populate keepalive auth cache on load too, so it's ready ASAP.
      authCache = {
        userId: user.id,
        accessToken: sessionData.session.access_token,
      };

      // Try composite key first
      let { data, error } = await supabase
        .from('bot_session_state')
        .select('session_data')
        .eq('user_id', user.id)
        .eq('strategy_id', sessionKey)
        .maybeSingle();

      // Fallback: try old key (just strategyId) for migration
      if ((error || !data?.session_data) && accountId) {
        const fallback = await supabase
          .from('bot_session_state')
          .select('session_data')
          .eq('user_id', user.id)
          .eq('strategy_id', strategyId)
          .maybeSingle();

        if (!fallback.error && fallback.data?.session_data) {
          data = fallback.data;
          error = fallback.error;
          console.log('[ChinWorkspace] Migrated from old session key to composite key');
        }
      }

      if (error || !data?.session_data) return null;

      const raw = data.session_data as Record<string, unknown>;
      if (raw.type !== 'chin') return null;

      // Restore Telegram alert toggles (default ON if missing)
      const alertToggles = raw.alertToggles as
        | { tma50AlertOn?: boolean; convergenceAlertOn?: boolean; boxVsTma50AlertOn?: boolean }
        | undefined;
      const tma50AlertOn = alertToggles?.tma50AlertOn ?? true;
      const convergenceAlertOn = alertToggles?.convergenceAlertOn ?? true;
      const boxVsTma50AlertOn = alertToggles?.boxVsTma50AlertOn ?? true;
      alertTogglesRef.current = { tma50AlertOn, convergenceAlertOn, boxVsTma50AlertOn };

      const rule4Enabled = typeof raw.rule4Enabled === 'boolean' ? raw.rule4Enabled : true;
      rule4EnabledRef.current = rule4Enabled;

      const ws = raw.workspace as Record<string, unknown> | undefined;
      if (!ws || !ws.selectedListId) return null;

      // Normalize legacy status values (level1/level2) → null so the 6-pattern
      // dropdown never renders an unknown key as a highlighted-but-empty label.
      const rawStatusMap = (ws.statusMap as Record<number, StatusLevel>) || {};
      const statusMap: Record<number, StatusLevel> = {};
      Object.entries(rawStatusMap).forEach(([key, value]) => {
        statusMap[Number(key)] = VALID_STATUS_LEVELS.has(value) ? (value as StatusLevel) : 'null';
      });

      return {
        selectedListId: (ws.selectedListId as string) || '',
        selectedAccountId: (raw.selectedAccountId as string) || '',
        statusMap,
        playMap: (ws.playMap as Record<number, boolean>) || {},
        symbolConfigs: (ws.symbolConfigs as Record<number, SymbolPlayConfig>) || {},
        tma50AlertOn,
        convergenceAlertOn,
        boxVsTma50AlertOn,
        rule4Enabled,
      };
    } catch {
      return null;
    }
  }, [sessionKey, strategyId, accountId]);

  // ─── Cleanup ──────────────────────────────────────────────

  const cancelPendingSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  }, []);

  // ─── Keepalive flush (unload-safe) ────────────────────────
  // Writes the latest workspace + alert toggles directly to the Supabase
  // REST endpoint using `fetch(..., { keepalive: true })`. Unlike the async
  // saveWorkspace path (which awaits getUser/upsert), this is SYNCHRONOUS and
  // the browser guarantees the request completes even if the tab is closed
  // right after toggling. Uses the cached auth (no await) + latestDataRef.
  const flushKeepalive = useCallback(() => {
    const data = latestDataRef.current;
    const auth = authCache;
    if (!data || !auth) return;

    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string | undefined;
    const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string | undefined;
    if (!supabaseUrl || !anonKey) return;

    const payload = {
      user_id: auth.userId,
      strategy_id: sessionKey,
      session_data: {
        type: 'chin',
        writerId: getDeviceWriterId(),
        selectedAccountId: data.selectedAccountId,
        wasRunning: true,
        lastActive: Date.now(),
        rule4Enabled: rule4EnabledRef.current,
        alertToggles: {
          tma50AlertOn: alertTogglesRef.current.tma50AlertOn,
          convergenceAlertOn: alertTogglesRef.current.convergenceAlertOn,
          boxVsTma50AlertOn: alertTogglesRef.current.boxVsTma50AlertOn,
        },
        workspace: {
          selectedListId: data.selectedListId,
          statusMap: data.statusMap,
          playMap: data.playMap,
          symbolConfigs: data.symbolConfigs,
        },
      },
      updated_at: new Date().toISOString(),
    };

    try {
      fetch(`${supabaseUrl}/rest/v1/bot_session_state?on_conflict=user_id,strategy_id`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          apikey: anonKey,
          Authorization: `Bearer ${auth.accessToken}`,
          Prefer: 'resolution=merge-duplicates',
        },
        body: JSON.stringify(payload),
      }).catch(() => { /* keepalive best-effort */ });
    } catch {
      // ignore — nothing more we can do during unload
    }
  }, [sessionKey]);

  // ─── Realtime cross-device sync ─────────────────────────
  // Listens for changes to this strategy+account workspace from OTHER
  // devices. When another device saves, reload the latest workspace and
  // notify the caller so the UI updates WITHOUT a manual refresh.
  // Uses the per-device writerId to skip our own writes echoing back.
  useEffect(() => {
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    const myWriterId = getDeviceWriterId();

    async function setup() {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId || cancelled) return;

      const handleChange = (payload: {
        new?: { strategy_id?: string; session_data?: Record<string, unknown> };
      }) => {
        const row = payload.new;
        if (!row || row.strategy_id !== sessionKey) return;
        const sd = row.session_data;
        if (!sd || sd.type !== 'chin') return;
        if (sd.writerId === myWriterId) return; // our own echo — ignore

        console.log('[ChinWorkspace] 🔄 Realtime change from another device, reloading...');
        loadWorkspace().then((data) => {
          if (!cancelled && data) onExternalChangeRef.current?.(data);
        });
      };

      channel = supabase
        .channel(`chin-ws-sync-${sessionKey}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'bot_session_state',
            filter: `user_id=eq.${userId}`,
          },
          handleChange,
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'bot_session_state',
            filter: `user_id=eq.${userId}`,
          },
          handleChange,
        )
        .subscribe();
    }

    setup();

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [sessionKey, loadWorkspace]);

  // ─── Register unload flush listeners ─────────────────────
  // pagehide + visibilitychange(hidden) are more reliable than beforeunload,
  // especially on mobile. Both fire flushKeepalive so the toggle survives
  // an immediate close.
  useEffect(() => {
    const onPageHide = () => flushKeepalive();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushKeepalive();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [flushKeepalive]);

  // Clear any pending "saved/error" reset timer on unmount.
  useEffect(() => {
    return () => {
      if (statusResetTimerRef.current) clearTimeout(statusResetTimerRef.current);
    };
  }, []);

  return {
    saveWorkspace,
    scheduleSave,
    saveImmediate,
    loadWorkspace,
    cancelPendingSave,
    flushKeepalive,
    saveStatus,
  };
}